// Per-line item entity tagging at Import Control (associated-entities.md → AE4).
// Tags a line item with the individual entities it spends on — Vehicle /
// Staff / Participant — for later per-entity cost rollups (AE5). Self-contained:
// each action is its own write (add / edit / remove) rather than riding the
// invoice form's save, so a mis-tag never blocks the invoice.
//
// Vehicle and Staff resolve through a picker into TYPED reference columns
// (api-contract §9): a Vehicle pick writes `vehicle_id`, the uuid of the
// `vehicles` row, and a Staff pick writes `staff_upn`. There is no single
// overloaded tag column — the schema's CHECK permits only the column matching
// `entity_type`, and the API ignores any other key, which is why the app's old
// `entity_id` made every hand-made tag a silent no-op (M9). `entity_name` carries
// the display value beside the reference (the fleet number, the person's name),
// which is what a resolved-by-worker row stores too, so a manual pick and a
// matched one read identically.
//
// The API stamps `match_source = 'Manual'` on every create and every edit here —
// a human touched the row. The pill still reads "Unmatched" when the row holds no
// reference, because that is the state a reviewer scanning for unresolved tags
// cares about (QA Q26); it is display logic over the data, not a second value.
//
// `entity_type` is FIXED at creation: §9.3 has no `entity_type` in its patch body,
// so on a saved row the type is shown as text rather than a dropdown that would
// look editable and change nothing.
//
// A NEW TAG IS A VEHICLE (owner, 2026-09-29, `docs/decisions.md` D-212): *"The
// solution is not yet ready for Staff and Participant associated entities -
// only Vehicles"*. The add form offers no type choice; it tags a vehicle. A
// Staff or Participant row already on a record still lists, edits (its name or
// staff pick) and removes exactly as before, so old data is not stranded.
//
// PORT NOTE — Participant is as-printed text only. The participant census was a
// direct read of a clinical SQL view; it is not part of this system, so there is
// no picker, no center scope and no resolution for a Participant tag. The
// document text is captured and that is the whole record.
//
// entity_source_text is the document text that triggered the reference,
// verbatim — an as-printed value, so like every other as-printed value in the
// schema it is never overwritten. On an existing row it is read-only: overtyping
// it would destroy the capture record and the key Staff/Vehicle resolution
// matches on.
//
// IT IS NO LONGER SHOWN OR ASKED FOR HERE (owner item 12, 2026-08-28: *"In the
// tagged entities box, get rid of the middle 'Source text' input. We just need
// the entity type and the entity itself."*). Two data columns, not three: the
// type, then the entity.
//
// THE COLUMN IS STILL WRITTEN AND STILL READ — it is not dead, and nothing came
// off the wire. Extraction populates it (`backend/src/lambda/extract.ts`),
// pass-2 vehicle resolution matches on it (`matchingLadder.ts`'s
// `resolveVehicleEntity`), the per-entity analytics rollup coalesces on it
// (`repo/pg/analytics.ts`), and the read-only listing here still falls back
// to it for a Participant when nothing else names one. What changed is only
// this editor: the add form no longer COLLECTS it, so a hand-added row simply has none — which is truthful, since a
// hand-added row never had document text to capture in the first place.
//
// WHAT THAT COST, AND HOW IT IS PAID: source text was the ONLY content a
// Participant row could carry, here and on the wire (there is no participant
// lookup — see the PORT NOTE above). So the entity column now holds a free-text
// NAME for a Participant instead of the picker the other two types get, written
// to `entity_name`; and an EXISTING Participant row displays
// `entity_name || entity_source_text`, so an AI-captured participant still reads
// out under the new layout rather than vanishing with the column.
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  Body1,
  Button,
  Caption1,
  Spinner,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components'
import { AddRegular, DeleteRegular } from '@fluentui/react-icons'
import type { AssociatedEntity, InvoiceLineItem, Invoice } from '../lib/types'
import {
  createAssociatedEntity,
  deactivateAssociatedEntity,
  loadAssociatedEntities,
  searchStaff,
  searchVehicles,
  updateAssociatedEntity,
} from '../lib/data'
import { errorMessage } from '../api/errors'
import { combinedEntities } from '../lib/lineItemBulk'
import { DotPill } from './Badges'
import { EntityPicker, type EntityOption } from './EntityPicker'
import { TINT_PILLS, useLineItemPanelStyles } from '../lib/theme'

/** Every type a STORED row may carry — what the list must still display. */
const ENTITY_TYPES = ['Vehicle', 'Staff', 'Participant'] as const
type EntityType = (typeof ENTITY_TYPES)[number]

/** The one type a person may ADD (D-212). */
const ADD_TYPE: EntityType = 'Vehicle'

/** Participant has no picker — nothing resolves a participant here. */
function hasPicker(type: EntityType): boolean {
  return type !== 'Participant'
}

// The picker's search, chosen by the row's entity type (searchVehicles /
// searchStaff already return the EntityPicker option shape).
function searchForType(type: EntityType): (term: string) => Promise<EntityOption[]> {
  if (type === 'Staff') return (t) => searchStaff(t)
  return (t) => searchVehicles(t)
}

function pickerPlaceholder(type: EntityType): string {
  return type === 'Staff' ? 'Search staff…' : 'Search vehicles…'
}

// The frame and heading (`root`, `head`, `headLabel`) are shared with the
// Additional Detail pop-up — `useLineItemPanelStyles` in
// `../lib/theme.ts` — so the two pop-ups on a line look alike.
const useStyles = makeStyles({
  none: { color: tokens.colorNeutralForeground3, fontStyle: 'italic' },
  list: { display: 'flex', flexDirection: 'column', gap: '6px' },
  // A tagged-entity row: type | entity | match-source pill | actions (owner
  // item 12, 2026-08-28 — the source-text column that sat between the first two
  // is gone). The entity column takes the width the two used to share.
  row: {
    display: 'grid',
    gridTemplateColumns: '128px minmax(0, 1fr) auto auto',
    alignItems: 'center',
    gap: '6px',
  },
  // A read-only text cell on an existing row — the entity TYPE, and, for a
  // Participant (which has no picker), the entity's own name. Sized and aligned
  // like the picker beside it so the row still reads as one line. Keeps its
  // name from the source-text column it used to style; that column is gone
  // (owner item 12, 2026-08-28).
  sourceText: {
    minWidth: 0,
    padding: '0 4px',
    fontSize: tokens.fontSizeBase200,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  sourceTextNone: { color: tokens.colorNeutralForeground3, fontStyle: 'italic' },
  // Stands in for the picker on a Participant row, which has no lookup.
  noPicker: {
    minWidth: 0,
    padding: '0 4px',
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
  },
  rowActions: { display: 'flex', alignItems: 'center', gap: '2px' },
  error: { color: tokens.colorPaletteRedForeground1 },
})

const matchSourcePill = (source: string | undefined) =>
  !source || source === 'Unmatched' ? TINT_PILLS.yellow : TINT_PILLS.green

function typeOf(v: string | undefined): EntityType {
  return (ENTITY_TYPES as readonly string[]).includes(v ?? '') ? (v as EntityType) : 'Vehicle'
}

/** The reference this row resolved to, read out of the column its type uses.
    A Participant row never has one — that is the whole design, not a gap. */
function referenceIdOf(ae: AssociatedEntity): string {
  const type = typeOf(ae.entity_type)
  if (type === 'Vehicle') return ae.vehicle_id ?? ''
  if (type === 'Staff') return ae.staff_upn ?? ''
  return ''
}

/** The typed reference column for `type`, as a write body. `null` clears the
    pick; the column for the OTHER type is sent as null on an edit so a row can
    never hold two references (the schema CHECK refuses it outright). */
function referenceFields(
  type: EntityType,
  entityId: string | undefined,
  mode: 'create' | 'edit',
): { vehicle_id?: string | null; staff_upn?: string | null } {
  const id = entityId || null
  if (type === 'Vehicle') return mode === 'create' && id === null ? {} : { vehicle_id: id }
  if (type === 'Staff') return mode === 'create' && id === null ? {} : { staff_upn: id }
  return {} // Participant: as-printed text only, no reference column
}

/** What the pill should say. The API records that a human set the row; a row with
    no reference is still unresolved, whatever set it (QA Q26). */
function displayedMatchSource(ae: AssociatedEntity): string {
  if (typeOf(ae.entity_type) === 'Participant') return ae.match_source ?? 'Unmatched'
  return referenceIdOf(ae) ? (ae.match_source ?? 'Manual') : 'Unmatched'
}

interface Props {
  invoice: Invoice
  lineItem: InvoiceLineItem
  /** Fired after a tag is added, edited or removed — never on the initial
      load, which would make a parent that re-renders on it loop. The line-item
      table uses it to re-read which lines still carry an UNMATCHED tag, so the
      red ring on the link button clears the moment the tag is resolved
      (owner, 2026-09-14). */
  onChanged?: () => void
  /** List the line's entities with no add, save or remove — the read-only
      steps (D-154). Every value is plain text; nothing renders locked. */
  readOnly?: boolean
  /** List the tags of every one of these lines, one per entity
      (`combinedEntities`), in place of `lineItem`'s — the line-item bulk row's
      proposed line (D-242). Pass it with `readOnly`. */
  childIds?: readonly string[]
}

export function AssociatedEntitiesEditor({ invoice, lineItem, onChanged, readOnly, childIds }: Props) {
  const s = useStyles()
  const ps = useLineItemPanelStyles()
  const headerId = invoice.id ?? ''
  const childId = lineItem.id
  const childIdsKey = childIds === undefined ? undefined : childIds.join('|')
  const [rows, setRows] = useState<AssociatedEntity[] | 'loading'>('loading')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Inline add form. It tags a Vehicle and nothing else (D-212).
  const [adding, setAdding] = useState(false)
  // The vehicle being tagged: `id` + display name from the picker.
  const [addEntity, setAddEntity] = useState<{ id?: string; name?: string }>({})
  // Monotonic load id so a slow refresh can't overwrite a newer one.
  const loadSeq = useRef(0)
  // THE HOST FOR EVERY POPUP IN HERE IS THIS EDITOR'S OWN ROOT, which is inside
  // the Popover. It used to be the invoice Dialog's surface, reached by
  // `.closest('[role="dialog"]')`, and that made the vehicle and staff pickers
  // unusable: a listbox mounted at the dialog is OUTSIDE the Popover, so
  // clicking one of its options was an outside-click, and Fluent's Popover
  // dismissed the whole editor before the pick could be committed. Every
  // hand-added row ended up `Unmatched` with an empty picker, and there was no
  // way to attach a vehicle at all.
  //
  // Mounting inside the editor keeps the stacking fix the dialog host was
  // chosen for (`popupPositionProps` + `usePopupListboxClass` raise the listbox
  // over the popover's own `zIndex: 10` surface) while putting the options
  // inside the surface that is listening for outside clicks.
  const rootRef = useRef<HTMLDivElement>(null)
  const [popupHost, setPopupHost] = useState<HTMLElement | null>(null)
  useEffect(() => {
    setPopupHost(rootRef.current)
  }, [])

  const refresh = useCallback(async () => {
    const seq = ++loadSeq.current
    if (!headerId) {
      setRows([])
      return
    }
    try {
      const all = await loadAssociatedEntities(headerId)
      if (seq !== loadSeq.current) return
      setRows(
        childIdsKey !== undefined
          ? combinedEntities(all, new Set(childIdsKey.split('|')))
          : all.filter((e) => e.child_id === childId),
      )
    } catch (e) {
      if (seq !== loadSeq.current) return
      setRows([])
      setError(`Could not load entities: ${errorMessage(e)}`)
    }
  }, [headerId, childId, childIdsKey])

  useEffect(() => {
    refresh()
  }, [refresh])

  // PORT NOTE: each action used to also write its own correction row for the
  // feedback loop. The API writes corrections itself now, from the change it
  // applied, so there is nothing to mirror here.

  async function handleAdd() {
    const type = ADD_TYPE
    const entityId = addEntity.id
    const entityName = (addEntity.name ?? '').trim()
    if (!entityId && !entityName) return // nothing meaningful to tag
    setBusy(true)
    setError(null)
    try {
      // NO `entity_source_text` (owner item 12, 2026-08-28 — this file's header
      // comment carries the whole rule). The API still accepts it on a create
      // and the column is still written by extraction; a HAND-ADDED row simply
      // has no document text to capture, so it sends none.
      await createAssociatedEntity(headerId, {
        child_id: childId,
        entity_type: type,
        entity_name: entityName || undefined,
        // The typed reference column for this type, and only that one.
        // `match_source` is not sent: §9.2 stamps 'Manual' on anything created
        // here, because a hand-added row is by definition a human's.
        ...referenceFields(type, entityId, 'create'),
      })
      setAdding(false)
      setAddEntity({})
      await refresh()
      onChanged?.()
    } catch (e) {
      setError(`Could not add entity: ${errorMessage(e)}`)
    } finally {
      setBusy(false)
    }
  }

  async function handleSaveRow(
    ae: AssociatedEntity,
    draft: { entity: { id?: string; name?: string } },
  ) {
    // The row's own type: §9.3 cannot change it, so the edit is the reference and
    // its display name. `entity_source_text` is not sent AT ALL — its mere
    // presence in a patch body is a 400, because it is as-printed provenance.
    const type = typeOf(ae.entity_type)
    const entityId = draft.entity.id ?? ''
    const entityName = draft.entity.name ?? ''
    setBusy(true)
    setError(null)
    try {
      await updateAssociatedEntity(ae.id, {
        // Empty clears the pick; a populated id resolves the row.
        ...referenceFields(type, entityId, 'edit'),
        entity_name: entityName,
        row_version: ae.row_version,
      })
      await refresh()
      onChanged?.()
    } catch (e) {
      setError(`Could not save entity: ${errorMessage(e)}`)
    } finally {
      setBusy(false)
    }
  }

  async function handleRemove(ae: AssociatedEntity) {
    setBusy(true)
    setError(null)
    try {
      await deactivateAssociatedEntity(ae.id, ae.row_version)
      await refresh()
      onChanged?.()
    } catch (e) {
      setError(`Could not remove entity: ${errorMessage(e)}`)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className={ps.root} ref={rootRef}>
      <div className={ps.head}>
        <span className={ps.headLabel}>Associated Entities</span>
        {rows === 'loading' && <Spinner size="tiny" />}
      </div>

      {error && <Caption1 className={s.error}>{error}</Caption1>}

      {rows !== 'loading' && rows.length === 0 && !adding && (
        <Body1 className={s.none}>No entities tagged on this line.</Body1>
      )}

      {rows !== 'loading' && rows.length > 0 && (
        <div className={s.list}>
          {rows.map((ae) => (
            <EntityRow
              key={ae.id}
              ae={ae}
              readOnly={readOnly}
              busy={busy}
              onSave={(draft) => handleSaveRow(ae, draft)}
              onRemove={() => handleRemove(ae)}
              popupHost={popupHost}
            />
          ))}
        </div>
      )}

      {readOnly ? null : adding ? (
        <div className={s.row}>
          {/* The type is fixed text, not a dropdown: a new tag is a Vehicle
              (D-212). Staff and Participant are no longer offered. */}
          <span className={s.sourceText} title={`Entity type: ${ADD_TYPE}`}>
            {ADD_TYPE}
          </span>
          <EntityPicker
            size="small"
            mountNode={popupHost}
            ariaLabel={`${ADD_TYPE} to tag`}
            value={addEntity}
            placeholder={pickerPlaceholder(ADD_TYPE)}
            search={searchForType(ADD_TYPE)}
            onSelect={(o) => setAddEntity({ id: o.id, name: o.primary })}
          />
          <Button size="small" appearance="primary" disabled={busy || !addEntity.id && !(addEntity.name ?? '').trim()} onClick={handleAdd}>
            Add
          </Button>
          <Button size="small" appearance="subtle" disabled={busy} onClick={() => { setAdding(false); setAddEntity({}) }}>
            Cancel
          </Button>
        </div>
      ) : (
        rows !== 'loading' && (
          <div>
            <Button size="small" appearance="subtle" icon={<AddRegular />} disabled={busy} onClick={() => setAdding(true)}>
              Add Entity
            </Button>
          </div>
        )
      )}
    </div>
  )
}

// A single existing tagged-entity row with its own draft state (seeded once from
// the loaded row). The Save button lights up only when the draft differs.
function EntityRow({
  ae,
  readOnly,
  busy,
  onSave,
  onRemove,
  popupHost,
}: {
  ae: AssociatedEntity
  /** Plain text in place of the picker, and no Save or Remove. */
  readOnly?: boolean
  busy: boolean
  onSave: (draft: { entity: { id?: string; name?: string } }) => void
  onRemove: () => void
  /** Where this row's picker mounts its listbox — the editor's own root, so an
      option click lands INSIDE the Popover. See the resolution above. */
  popupHost: HTMLElement | null
}) {
  const s = useStyles()
  // FIXED, not a draft: §9.3 carries no `entity_type`, so the type a row was
  // created with is the type it keeps.
  const type = typeOf(ae.entity_type)
  // A Participant carries no reference to resolve, so what it shows is its own
  // name — falling back to the as-printed source text, which is what an
  // AI-captured participant actually holds and was all the removed source-text
  // column ever displayed for one (owner item 12, 2026-08-28). Read straight
  // off the row, never held as draft state: nothing here edits either value.
  const participantLabel = (ae.entity_name ?? '').trim() || (ae.entity_source_text ?? '').trim()
  const [entity, setEntity] = useState<{ id?: string; name?: string }>(() => ({
    id: referenceIdOf(ae) || undefined,
    name: ae.entity_name || undefined,
  }))

  // Adopt server-side changes (e.g. the directory worker resolving an entity
  // after load) into the draft — but ONLY while the draft still matched the
  // PREVIOUS server row, so an in-progress edit is never clobbered.
  const prevAeRef = useRef(ae)
  useEffect(() => {
    const prev = prevAeRef.current
    prevAeRef.current = ae
    const serverChanged =
      referenceIdOf(ae) !== referenceIdOf(prev) ||
      (ae.entity_name ?? '') !== (prev.entity_name ?? '')
    if (!serverChanged) return
    const wasUntouched =
      (entity.id ?? '') === referenceIdOf(prev) &&
      (entity.name ?? '') === (prev.entity_name ?? '')
    if (!wasUntouched) return
    setEntity({ id: referenceIdOf(ae) || undefined, name: ae.entity_name || undefined })
  }, [ae, entity])

  const dirty =
    (entity.id ?? '') !== referenceIdOf(ae) ||
    (entity.name ?? '') !== (ae.entity_name ?? '')

  return (
    <div className={s.row}>
      <span className={s.sourceText} title={`Entity type: ${type}`}>
        {type}
      </span>
      {/* NO SOURCE-TEXT CELL (owner item 12, 2026-08-28) — see this file's
          header comment. A Participant, which has no picker, shows its own
          name here instead, falling back to the captured source text so an
          AI-tagged participant still reads out. */}
      {readOnly ? (
        <span
          className={mergeClasses(s.sourceText, !(ae.entity_name || participantLabel) && s.sourceTextNone)}
          title={(ae.entity_name ?? '').trim() || participantLabel || undefined}
        >
          {(ae.entity_name ?? '').trim() || participantLabel || 'No name recorded'}
        </span>
      ) : hasPicker(type) ? (
        <EntityPicker
          key={type}
          size="small"
          mountNode={popupHost}
          ariaLabel={`${type} to tag`}
          value={entity}
          placeholder={pickerPlaceholder(type)}
          search={searchForType(type)}
          onSelect={(o) => setEntity({ id: o.id, name: o.primary })}
        />
      ) : (
        <span
          className={mergeClasses(s.sourceText, !participantLabel && s.sourceTextNone)}
          title={participantLabel || undefined}
        >
          {participantLabel || 'No name recorded'}
        </span>
      )}
      <DotPill pill={matchSourcePill(displayedMatchSource(ae))} title={`Match source: ${displayedMatchSource(ae)}`}>
        {displayedMatchSource(ae)}
      </DotPill>
      <div className={s.rowActions}>
        {!readOnly && dirty && (
          <Button size="small" appearance="primary" disabled={busy} onClick={() => onSave({ entity })}>
            Save
          </Button>
        )}
        {!readOnly && (
          <Button size="small" appearance="subtle" icon={<DeleteRegular />} aria-label="Remove Entity" disabled={busy} onClick={onRemove} />
        )}
      </div>
    </div>
  )
}
